import { normalizeBlockMathDelimiters } from '../../components/utils/contentConverter';

// Anchored to the start of the candidate slice: the trailing-image pattern always
// runs to the end of the string, so every candidate begins at a literal `![`.
// Deliberately lookbehind-free — Safari only gained RegExp lookbehind in 16.4, and
// esbuild silently rewrites an unsupported literal into a top-level `new RegExp(…)`
// call, turning a build-time gap into a module-evaluation crash on load (PLA-117).
const INCOMPLETE_IMAGE_MARKDOWN_TAIL_REGEX = /^!\[[^\]\n]*(?:\](?:\[[^\]\n]*|\([^)\n]*)?)?$/;

function isInsideOpenCodeContext(prefix: string): boolean {
	const fenceMatches = prefix.match(/```/g);
	if (fenceMatches && fenceMatches.length % 2 === 1) return true;

	const lastLineStart = prefix.lastIndexOf('\n') + 1;
	const lastLine = prefix.slice(lastLineStart);
	let inlineBacktickCount = 0;
	for (let i = 0; i < lastLine.length; i++) {
		if (lastLine[i] === '`' && (i === 0 || lastLine[i - 1] !== '\\')) inlineBacktickCount++;
	}
	return inlineBacktickCount % 2 === 1;
}

function findIncompleteTrailingImageIndex(text: string): number {
	// Scan `![` candidates left to right so the first *unescaped* one wins, matching
	// how the engine used to backtrack past a failed lookbehind. Rejecting only the
	// leftmost match would miss a valid image later in the line (`\![a and ![b`).
	for (let i = text.indexOf('!['); i !== -1; i = text.indexOf('![', i + 1)) {
		// A single preceding backslash escapes the bang. This intentionally also skips
		// `\\![` (an escaped backslash, so the bang is not really escaped) to preserve
		// the previous expression's behavior exactly; see the test pinning that case.
		if (text[i - 1] === '\\') continue;
		if (!INCOMPLETE_IMAGE_MARKDOWN_TAIL_REGEX.test(text.slice(i))) continue;
		if (isInsideOpenCodeContext(text.slice(0, i))) return -1;
		return i;
	}
	return -1;
}

/**
 * True when `text` ends with an unfinished markdown image (e.g. `![alt`,
 * `![alt](http`). Skips matches inside open code fences or inline code spans.
 * Callers use this to decide whether to show an image-generation indicator
 * while the markdown is mid-stream.
 */
export function hasIncompleteTrailingImageMarkdown(text: string): boolean {
	return findIncompleteTrailingImageIndex(text) !== -1;
}

/**
 * Preprocesses markdown text for rendering with ReactMarkdown.
 * Adds trailing spaces for line breaks (except inside math blocks)
 * and normalizes block math delimiters.
 *
 * When `isStreaming` is true, a trailing incomplete image markdown fragment
 * is stripped so the raw syntax isn't visible to the user mid-stream. A
 * loading indicator should be shown separately by the caller (see
 * `hasIncompleteTrailingImageMarkdown`). Completed `![alt](url)` is
 * untouched and rendered as a real image.
 *
 * @param text - The markdown text to preprocess
 * @param isStreaming - Whether the text is currently being streamed
 * @returns Preprocessed markdown text ready for ReactMarkdown
 */
export function preprocessMarkdownText(text: string, isStreaming = false): string {
	let source = text;
	if (isStreaming) {
		const index = findIncompleteTrailingImageIndex(text);
		if (index !== -1) source = text.slice(0, index);
	}
	// Normalize $$ to be on their own lines so remark-math can parse block math
	const normalized = normalizeBlockMathDelimiters(source);
	const lines = normalized.split('\n');
	let inMathBlock = false;

	return lines
		.map((line) => {
			const trimmed = line.trim();
			if (trimmed === '$$') {
				inMathBlock = !inMathBlock;
				return line;
			}
			// Add trailing spaces for markdown line breaks, but not inside math blocks
			return inMathBlock ? line : line + '  ';
		})
		.join('\n');
}
